import { z } from 'zod';
import type {
  CreateOrganizationRequest,
  Organization,
  UpdateOrganizationRequest,
} from '@repo/api-client/dtos';

const COMMON_TIMEZONES = ['UTC'];
void COMMON_TIMEZONES;

/**
 * Business (organization) form. Branding & nested contact details are
 * stored as records on the wire; we model them as flat string fields
 * here so react-hook-form has clean control names. The serializers
 * below pack/unpack the object shapes.
 */
export const businessFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  nickname: z.string().trim().min(1).optional().or(z.literal('')),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  currency: z.string().min(1).max(3).default('USD'),
  dateFormat: z.string().min(1).default('MM/DD/YYYY'),
  timezone: z.string().min(1).default('UTC'),
  invoicePrefix: z.string().trim().min(1).default('INV'),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().trim().optional().or(z.literal('')),
  website: z.string().trim().optional().or(z.literal('')),
  addressLine1: z.string().trim().optional().or(z.literal('')),
  addressLine2: z.string().trim().optional().or(z.literal('')),
  city: z.string().trim().optional().or(z.literal('')),
  region: z.string().trim().optional().or(z.literal('')),
  postalCode: z.string().trim().optional().or(z.literal('')),
  country: z.string().trim().optional().or(z.literal('')),
  taxNumber: z.string().trim().optional().or(z.literal('')),
  businessNumber: z.string().trim().optional().or(z.literal('')),
  registrationNumber: z.string().trim().optional().or(z.literal('')),
});

export type BusinessFormValues = z.infer<typeof businessFormSchema>;

export function businessFormDefaults(): BusinessFormValues {
  return {
    name: '',
    nickname: '',
    description: '',
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        : 'UTC',
    invoicePrefix: 'INV',
    email: '',
    phone: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
    taxNumber: '',
    businessNumber: '',
    registrationNumber: '',
  };
}

export function businessFormFromOrganization(org: Organization): BusinessFormValues {
  const contact = (org.contact ?? {}) as Record<string, unknown>;
  const address = (contact['address'] ?? {}) as Record<string, unknown>;
  return {
    name: org.name,
    nickname: org.nickname ?? '',
    description: org.description ?? '',
    currency: org.currency,
    dateFormat: org.dateFormat,
    timezone: org.timezone,
    invoicePrefix: org.invoicePrefix,
    email: typeof contact['email'] === 'string' ? (contact['email'] as string) : '',
    phone: typeof contact['phone'] === 'string' ? (contact['phone'] as string) : '',
    website: typeof contact['website'] === 'string' ? (contact['website'] as string) : '',
    addressLine1: typeof address['line1'] === 'string' ? (address['line1'] as string) : '',
    addressLine2: typeof address['line2'] === 'string' ? (address['line2'] as string) : '',
    city: typeof address['city'] === 'string' ? (address['city'] as string) : '',
    region: typeof address['region'] === 'string' ? (address['region'] as string) : '',
    postalCode: typeof address['postalCode'] === 'string' ? (address['postalCode'] as string) : '',
    country: typeof address['country'] === 'string' ? (address['country'] as string) : '',
    taxNumber: org.taxNumber ?? '',
    businessNumber: org.businessNumber ?? '',
    registrationNumber: org.registrationNumber ?? '',
  };
}

function buildContact(values: BusinessFormValues): Record<string, unknown> | undefined {
  const address: Record<string, unknown> = {};
  if (values.addressLine1) address['line1'] = values.addressLine1;
  if (values.addressLine2) address['line2'] = values.addressLine2;
  if (values.city) address['city'] = values.city;
  if (values.region) address['region'] = values.region;
  if (values.postalCode) address['postalCode'] = values.postalCode;
  if (values.country) address['country'] = values.country;
  const contact: Record<string, unknown> = {};
  if (values.email) contact['email'] = values.email;
  if (values.phone) contact['phone'] = values.phone;
  if (values.website) contact['website'] = values.website;
  if (Object.keys(address).length) contact['address'] = address;
  return Object.keys(contact).length ? contact : undefined;
}

export function businessFormToCreate(values: BusinessFormValues): CreateOrganizationRequest {
  const contact = buildContact(values);
  return {
    name: values.name,
    ...(values.nickname ? { nickname: values.nickname } : {}),
    ...(values.description ? { description: values.description } : {}),
    currency: values.currency,
    dateFormat: values.dateFormat,
    timezone: values.timezone,
    invoicePrefix: values.invoicePrefix,
    ...(contact ? { contact } : {}),
    ...(values.taxNumber ? { taxNumber: values.taxNumber } : {}),
    ...(values.businessNumber ? { businessNumber: values.businessNumber } : {}),
    ...(values.registrationNumber ? { registrationNumber: values.registrationNumber } : {}),
  };
}

export function businessFormToUpdate(values: BusinessFormValues): UpdateOrganizationRequest {
  const contact = buildContact(values);
  return {
    name: values.name,
    nickname: values.nickname || undefined,
    description: values.description || undefined,
    currency: values.currency,
    dateFormat: values.dateFormat,
    timezone: values.timezone,
    invoicePrefix: values.invoicePrefix,
    contact,
    taxNumber: values.taxNumber || undefined,
    businessNumber: values.businessNumber || undefined,
    registrationNumber: values.registrationNumber || undefined,
  };
}
